import ImageTextureLayer from './ImageTextureLayer';
import ControlsLayer from './ControlsLayer';
import { ImagePosition, LayerPosition, LocalLayerPosition, MaskLayerProps } from '../types/layer';
import CollisionDetector from '../utils/CollisionDetector';

export default class MaskTextureLayer extends ImageTextureLayer {
  private readonly maskImage;
  private totalVisiblePixels = 0;
  public checkMouse;
  // For mousemat mask box position
  public sizeBoxMask_XTop;
  public sizeBoxMask_XBottom;
  public sizeBoxMask_YLeft;
  public sizeBoxMask_YRight;
  public sizeControlBoxTop;
  public sizeControlBoxBottom;
  public sizeControlResizeRight;
  public sizeControlResizeLeft;
  public sizeControlRotateRight;
  public sizeControlRotateLeft;
  // End mousemat mask box position
  controls: ControlsLayer;
  public centered;
  public layerId;
  public color;
  public layerCenterX;
  public productId;

  active = false;
  hover = false;
  drag = false;

  resizeLimit = {
    maxW: 1350,
    maxH: 1450,
    minW: 50,
    minH: 35
  };

  constructor({ maskImage, w, h, position, checkMouse, ...props }: MaskLayerProps) {
    super({ w: maskImage.width, h: maskImage.height, ...props });
    this.maskImage = maskImage;
    this.scale = 1;
    this.checkMouse = checkMouse;
    this.rotation = this.checkMouse? 0 : 0;
    this.controls = new ControlsLayer(this);

    this.setCorrectSize(w,h);
    this.setCorrectPosition();
    this.position = {
      ...position
    }
  }

  setImagePosition(position, mousePosition, originPosition) {
    const activeControl = this.controls.getActive();

    if (activeControl) {
      this.controls.update(this.toImagePosition(mousePosition), originPosition);
    } else {
      if (this.position.x !== position.x || this.position.y !== position.y) {
        this.needsRender = true;
      }

      this.position = {
        x: position.x,
        y: position.y,
      };
    }
  }

  setScale(scale) {
    // Bounds scale
    scale = Math.max(
      // Width scale
        Math.min(this.resizeLimit.maxW, Math.max(this.image.width * scale, this.resizeLimit.minW)) / this.image.width,
      // Height scale
        Math.min(this.resizeLimit.maxH, Math.max(this.image.height * scale, this.resizeLimit.minH)) / this.image.height,
    );

    const size = this.getImageSize(scale);
    // this.position.x -= (size.width);
    // this.position.y -= (size.height);
    this.scale = scale;
    this.controls.setSize(size);
    this.needsRender = true;
  }

  setActive(active) {
    if (active !== this.active) {
      this.needsRender = true;
    }
    if(active)document.body.style.cursor = "move";
    else document.body.style.cursor = "default";
    this.active = active;
  }

  setHover(hover) {
    if(hover && !this.active) document.body.style.cursor = "pointer";
    else if(hover && this.active) document.body.style.cursor = "move";
    else document.body.style.cursor = "default";
    this.hover = hover;
  }

  setDrag(drag) {
    this.controls.setDrag(drag);
    if (drag !== this.drag) {
      this.needsRender = true;
    }
    this.drag = drag;
  }

  hit(mouse: LocalLayerPosition) {
    return (this.active && this.controls.raycast(this.toImagePosition(mouse))) ||
        (
            // For mousemat
            this.active && this.controls.raycastMouseMat(mouse)
        )
      || (
        // 2-step hit detection
        // we hit the bounding -> check pixel-perfect
        CollisionDetector.boxHitTest(this.toImagePosition(mouse), this.getImageSize())
        && CollisionDetector.pixelHitTest(this.canvas, mouse)
      ) || ( CollisionDetector.boxHitMouseMatTest(mouse,this.getImageSize(),this.sizeBoxMask_XTop,this.sizeBoxMask_XBottom,this.sizeBoxMask_YLeft,this.sizeBoxMask_YRight)
            && CollisionDetector.pixelHitTest(this.canvas, mouse))
        || (CollisionDetector.boxHitMouseMatTest(mouse,this.getImageSize(),this.sizeBoxMask_XTop,this.sizeBoxMask_XBottom,this.sizeBoxMask_YLeft,this.sizeBoxMask_YRight))
  }

  setCorrectSize(width = 200, height = 250 ) {
    this.setScale(Math.min(width / this.image.width, height / this.image.height));
    this.controls.setSize(this.getImageSize());
  }

  setCorrectPosition() {
    const isBlank = !(
      new Uint32Array(this.context.getImageData(0, 0, this.width, this.height).data.buffer)
    ).some(color => color !== 0);

    if (isBlank) {
      // Set image to center
      const { width, height } = this.getImageSize();
      this.position = {
        x: this.width / 2 - width / 2,
        y: this.height / 2 - (this.checkMouse? this.image.height * this.scale : height) / 0.5,
      }
    }
  }

  // For mousemat
  setMaskMousePosition (scale,size) {
    if(this.checkMouse){
      const sizeMouse = this.getImageSize(scale);
      this.sizeBoxMask_XTop = (this.position.x + sizeMouse.width * 0.5) - (sizeMouse.height/2);
      this.sizeBoxMask_XBottom = (this.position.x + sizeMouse.width * 0.5) + (sizeMouse.height/2);
      this.sizeBoxMask_YRight = (this.position.y + sizeMouse.height * 0.5) - (sizeMouse.width/(size === "xl" || size === "XL" || size[0] === "xl"? 2:1));
      this.sizeBoxMask_YLeft = (this.position.y + sizeMouse.height * 0.5) + (sizeMouse.width/(size === "xl" || size === "XL" ||size[0] === "xl"? 2:1));
      this.sizeControlBoxTop = (this.position.x + sizeMouse.width * 0.5) - (sizeMouse.height/2) - 200;
      this.sizeControlBoxBottom = (this.position.x + sizeMouse.width * 0.5) - (sizeMouse.height/2);
      this.sizeControlResizeRight = this.sizeBoxMask_YLeft - 38;
      this.sizeControlResizeLeft = this.sizeBoxMask_YLeft + 38;
      this.sizeControlRotateRight = this.sizeBoxMask_YRight - 38;
      this.sizeControlRotateLeft = this.sizeBoxMask_YRight + 38;
    }
  }

  showLine(id, color = '#000') {
    let layerPositionX = this.controls.imageLayer.position.x;

    const layerWidth = this.controls.width - 20;
    const layerCenterX = layerPositionX + (layerWidth / 2) - 8;
    const yPosTop = this.position.y;
    const yPosBottom = this.position.y + this.height;

    this.context.setLineDash([8, 8]);
  
    this.context.strokeStyle = color;
    this.context.lineWidth = 2;
    this.context.beginPath();
    this.context.moveTo(layerCenterX, yPosTop);
    this.context.lineTo(layerCenterX, yPosBottom);
    this.context.stroke();
  
    const canvasTopY = 0;
    this.context.beginPath();
    this.context.moveTo(layerCenterX, yPosTop);
    this.context.lineTo(layerCenterX, canvasTopY);
    this.context.stroke();
  
    this.context.setLineDash([]);

    this.controls.showDiagonalLine(color);
  }

  getPosition() {
    let { x, y } = this.controls.getOffset();
    return {
      x: this.position.x - x,
      y: this.position.y - y,
    };
  }

  toImagePosition(mouse: LayerPosition): ImagePosition {
    return {
      x: mouse.x - this.position.x,
      y: mouse.y - this.position.y,
    }
  }

  loadJSON({ position, scale, rotate }) {
    this.rotation = rotate;
    this.position = { x: position[0], y: position[1] };
    this.setScale(scale);
  }

  getImageCenterX(productType, layerId) {
    const hoodieValues = {
      image_front: 985,
      image_back: 330,
      image_right: 970,
      image_left: 440,
      default: 460
    };
  
    const tshirtValues = {
      image_front: 460,
      image_back: 1398,
      image_right: 1190,
      image_left: 1690,
      default: 460
    };
  
    const values = productType === '1207887' ? hoodieValues : tshirtValues;
  
    if (layerId.startsWith('image_front')) {
      return values.image_front;
    } else if (layerId.startsWith('image_back')) {
      return values.image_back;
    } else if (layerId.startsWith('image_right')) {
      return values.image_right;
    } else if (layerId.startsWith('image_left')) {
      return values.image_left;
    } else {
      return values.default;
    }
  }
  
  isLayerCentered(layer, threshold = 20) {
    let imageCenterX = this.getImageCenterX(this.productId, this.layerId)
    let layerPositionX = layer.imageLayer.position.x;

    const layerWidth = layer.width - 45;
    const layerCenterX = layerPositionX + (layerWidth / 2);
    const isXClose = Math.abs(layerCenterX - imageCenterX) < threshold;

    if (isXClose) {
      const fixedLeftPosition = imageCenterX - (layerWidth / 2);
      this.controls.imageLayer.position.x = fixedLeftPosition;
    }
  
    return {
      isXClose,
      layerCenterX
    };
  }
  
  
  public setCentered() {
    const { isXClose, layerCenterX } = this.isLayerCentered(this.controls);
    this.layerCenterX = layerCenterX;
    this.centered = isXClose;

    if (isXClose && this.active) {
      this.showLine(this.layerId, this.color);
    }
  }

  setAdditionalLayerProps(id = 'image_front', color = '#000', productId) {
    this.layerId = id;
    this.color = color;
    this.productId = productId;
  }

  render(size ="XL") {
    if (!super.render(size, this.active)) return false;
    //For mousemat mask
    this.setMaskMousePosition(this.scale,size);
    // End
    if (this.layerId && (this.productId === '1206225' || this.productId === '1207887')) {
      this.setCentered()
    }

    this.controls.render(size);
    this.context.save();
    if (this.drag) {
      this.context.globalAlpha = 0.8;
    }

    this.context.restore();

    return true;
  }
}
